import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getMyAppointment } from '../../api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { ArrowLeft, CalendarDays, Clock, FileText, DollarSign } from 'lucide-react';
import { getApiErrorMessage } from '../../utils/apiError';
import { toast } from 'sonner';

const statusLabels = {
  PENDIENTE: 'Pendiente',
  CONFIRMADA: 'Confirmada',
  CANCELADA: 'Cancelada',
  NO_SHOW: 'No asistió',
  COMPLETADA: 'Completada',
  REQUIERE_REAGENDAR: 'Por reagendar',
};

const paymentLabels = {
  sin_pagar: 'Sin pagar',
  pagado: 'Pagado',
};

export default function PatientAppointmentShow() {
  const { id } = useParams();
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getMyAppointment(id);
        const data = res.data?.data ?? res.data;
        setAppointment(data);
      } catch (e) {
        toast.error(getApiErrorMessage(e, 'Error al cargar la cita'));
      } finally {
        setLoading(false);
      }
    };
    if (id) load();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="w-10 h-10 border-4 border-[#335C67] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!appointment) {
    return (
      <div className="space-y-4">
        <Button asChild variant="ghost" size="sm">
          <Link to="/mis-citas"><ArrowLeft className="w-4 h-4 mr-2" /> Volver a mis citas</Link>
        </Button>
        <p className="text-[#5D5C61]">No se encontró la cita.</p>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('es-MX', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button asChild variant="ghost" size="sm">
          <Link to="/mis-citas"><ArrowLeft className="w-4 h-4 mr-2" /> Volver a mis citas</Link>
        </Button>
      </div>

      <Card className="border-slate-100">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-xl flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-[#335C67]" />
              Detalle de la cita
            </CardTitle>
            <Badge className={
              appointment.status === 'CONFIRMADA' ? 'bg-[#588157]/10 text-[#588157]' :
              appointment.status === 'CANCELADA' ? 'bg-[#9E2A2B]/10 text-[#9E2A2B]' :
              appointment.status === 'COMPLETADA' ? 'bg-[#335C67]/10 text-[#335C67]' :
              'bg-[#E09F3E]/10 text-[#E09F3E]'
            }>
              {statusLabels[appointment.status] || appointment.status}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3 text-[#2B2D42]">
            <Clock className="w-5 h-5 text-[#8D99AE]" />
            <div>
              <p className="font-medium">{formatDate(appointment.date)}</p>
              <p className="text-[#5D5C61]">
                {appointment.start_time} - {appointment.end_time}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-[#2B2D42]">
            <span className="text-sm text-[#8D99AE]">Tipo:</span>
            <span className="capitalize">{appointment.type === 'virtual' ? 'Virtual' : 'Presencial'}</span>
          </div>
          {appointment.status === 'CANCELADA' && appointment.cancellation_reason && (
            <div className="flex items-start gap-3 text-[#2B2D42] rounded-lg border border-[#9E2A2B]/20 bg-[#9E2A2B]/5 p-3">
              <div>
                <p className="text-sm text-[#8D99AE]">Motivo de cancelación</p>
                <p className="text-[#9E2A2B]">{appointment.cancellation_reason}</p>
              </div>
            </div>
          )}
          {appointment.reason && (
            <div className="flex items-start gap-3 text-[#2B2D42]">
              <FileText className="w-5 h-5 text-[#8D99AE] shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-[#8D99AE]">Motivo</p>
                <p>{appointment.reason}</p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-3 text-[#2B2D42]">
            <DollarSign className="w-5 h-5 text-[#8D99AE]" />
            <div>
              <p className="text-sm text-[#8D99AE]">Estado de pago</p>
              <p>{paymentLabels[appointment.payment_status] || appointment.payment_status}</p>
            </div>
          </div>
          {appointment.notes && (
            <div className="pt-2 border-t border-slate-100">
              <p className="text-sm text-[#8D99AE]">Notas</p>
              <p className="text-[#5D5C61]">{appointment.notes}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
